import { useCallback } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { useBlockingInvoiceError } from 'hooks/athletePackage/useBlockingInvoiceError';
import { UseMutationResult, useQueryClient } from 'react-query';
import { AthletePackage, AthletePackageCancel, ErrorMessage } from 'types';
import notificationHandler from 'utils/notifications';

import { CancelFormValues } from './useCancelForm';

export type CancelMutation = UseMutationResult<
  AxiosResponse<AthletePackage>,
  AxiosError<ErrorMessage>,
  AthletePackageCancel
>;

interface UseCancelSubmissionParams {
  values: CancelFormValues;
  cancelMutation: CancelMutation;
  onSuccess: () => void;
}

interface CancelSubmission {
  submit: () => void;
  showErrorModal: boolean;
  errorModalData: ReturnType<typeof useBlockingInvoiceError>['errorModalData'];
  resetError: () => void;
}

export function buildCancelPayload(values: CancelFormValues): AthletePackageCancel {
  const { cancelNow, cancellationDate, athleteStatusOnCancel, cancellationReason, reservationChoice } =
    values;
  return {
    cancelNow,
    cancellationDate:
      cancelNow || cancellationDate === null ? null : dayjs(cancellationDate).format('YYYY-MM-DD'),
    athleteStatusOnCancel: athleteStatusOnCancel === 'No status change' ? null : athleteStatusOnCancel,
    cancellationReason,
    retainReservations: reservationChoice !== 'DELETE',
  };
}

export default function useCancelSubmission({
  values,
  cancelMutation,
  onSuccess,
}: UseCancelSubmissionParams): CancelSubmission {
  const queryClient = useQueryClient();
  const { successNotification } = notificationHandler();
  const { showErrorModal, errorModalData, handleError, resetError } = useBlockingInvoiceError({
    nonBlockingErrorTitle: 'Athlete package cancel failed',
  });

  const submit = useCallback(() => {
    cancelMutation.mutate(buildCancelPayload(values), {
      onError: handleError,
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']);
        queryClient.invalidateQueries(['athleteEvents']);
        successNotification('Athlete package cancel success', 'Athlete package successfully cancelled.');
        resetError();
        onSuccess();
      },
    });
  }, [values, cancelMutation, handleError, queryClient, successNotification, resetError, onSuccess]);

  return { submit, showErrorModal, errorModalData, resetError };
}
